export const getModelVersion = (args) => {
  const { details } = args ?? {}

  // Since gpt-4 could only handle 8k tokens. We would use gpt-4-32k as it supports 32k tokens, so we could handle more room rates
  const gptVersion4_32k = '4_32k'
  const gptVersion4 = '4'

  return details ? gptVersion4 : gptVersion4_32k
}

export const getInfoForLogging = (details, pageSource) => {
  if (typeof window !== 'undefined') {
    if (pageSource === 'listings') {
      const {
        travelDate: departureDate,
        tripType,
        returnDate: arrivalDate,
        numOfAdults,
        numOfChildren,
        numOfLapInfants,
      } = details || {}
      return {
        arrivalDate,
        departureDate,
        numOfChildren,
        numOfAdults,
        numOfLapInfants,
        airlineName: '',
        airlineId: '',
        airlineInfo: '',
        currentPageURL: String(new URL(window.location.href)),
        tripType,
      }
    } else {
      const { passengers, airline, slices } = details || {}
      const slicesLen = slices?.length
      const arrivalDate = slices?.[slicesLen - 1]?.arrivalInfo?.time?.dateTime?.toString()
      return {
        arrivalDate: arrivalDate,
        departureDate: slices?.[0]?.departInfo?.time?.dateTime?.toString(),
        numOfChildren: passengers?.CHILD,
        numOfAdults: passengers?.ADULT,
        numOfLapInfants: passengers?.INFANT,
        airlineName: airline?.[0]?.name ?? '',
        airlineId: airline?.[0]?.code ?? '',
        currentPageURL: String(new URL(window.location.href)),
      }
    }
  }
}

const getSegments = (segments) => {
  const result = segments.map((item, i) => {
    const { seatsAvailable, departInfo, arrivalInfo, duration, flightNumber, cabinName, cabinClass } =
      item || {}
    const { airport: departAirport, time: departTime } = departInfo || {}
    const { airport: arrivalAirport, time: arrivalTime } = arrivalInfo || {}

    return `
    Segment #${i + 1}. depart airport: ${departAirport?.name ? `${departAirport.name}, ` : ''}${
      departAirport?.code ? `${departAirport.code}` : ``
    }${departAirport.displayLocation ? `, ${departAirport.displayLocation}` : ''},
    depart time: ${departTime?.dateTime || ''},
    arrival airport: ${arrivalAirport?.name ? `${arrivalAirport.name}, ` : ''}${
      arrivalAirport?.code ? `${arrivalAirport.code}` : ''
    }${arrivalAirport.displayLocation ? `, ${arrivalAirport.displayLocation}` : ''},
    arrival time: ${arrivalTime?.dateTime || ''},
    cabin class: ${cabinName || cabinClass || ''},
    duration of flight is ${duration || ''} minutes,
    flight number: ${flightNumber || ''},
    seats available: ${seatsAvailable || ''}
    `
  })

  return result.join(',')
}

const getFlightInfo = (slices = [], listingsPageInfo) => {
  const { pageSource, marketingAirlines } = listingsPageInfo || {}
  const result = (slices || []).map((item, i) => {
    const { displayAirline, departInfo, arrivalInfo, duration, isOvernight, stops, segments } = item || {}
    const { airport: departAirport, time: departTime } = departInfo || {}
    const { airport: arrivalAirport, time: arrivalTime } = arrivalInfo || {}
    const { marketingAirline } = segments[0] || {}
    const listingsMarketingAirline = (marketingAirlines || [])?.find(({ code }) => code === marketingAirline)
    const displayAirlineDetails = pageSource === 'listings' ? listingsMarketingAirline : displayAirline
    return `
    Leg #${i + 1}. depart airport: ${departAirport?.name ? `${departAirport.name}, ` : ''}${
      departAirport?.code ? `${departAirport?.code}, ` : ''
    }${departAirport?.displayLocation ? `${departAirport.displayLocation}` : ''},
    depart time: ${departTime?.dateTime || ''},
    arrival airport: ${arrivalAirport.name ? `${arrivalAirport.name}, ` : ''}${
      arrivalAirport?.code ? `${arrivalAirport.code}, ` : ''
    }${arrivalAirport?.displayLocation ? `${arrivalAirport.displayLocation}` : ''},
    arrival time: ${arrivalTime?.dateTime || ''},
    Airline is ${displayAirlineDetails?.name || ''}, code is ${displayAirlineDetails?.code || ''},
    duration of flight is ${duration || ''},
    is this flight overnight: ${isOvernight || ''},
    This flight has ${stops || ''} stops,
    Further details of this leg of the trip: ${getSegments(segments)}
    `
  })

  return result.join(',')
}

const getFlightExpressDealInfo = (slices) => {
  const result = (slices || []).map((slice, i) => {
    return `
    ${i}. depart airport is ${slice?.origin?.code || ''},
    depart time between ${slice?.departureWindow?.start || ''} and ${slice?.departureWindow?.end || ''},
    arrival airport is ${slice?.destination?.code || ''},
    arrival time between ${slice?.arrivalWindow?.start || ''} and ${slice?.arrivalWindow?.end || ''},
    cabin class is ${slice?.cabinClass || ''}
    maximum number of stops are ${slice?.maximumStops || ''}
    carry on Baggage is ${slice?.carryOnBagOption || ''}
    `
  })
  return result.join(',')
}

const getFlightPrice = (price, flightMetaInfo = {}) => {
  if (Array.isArray(price)) {
    const priceDetails = (price || []).map(({ name, price: priceArr }, idx) => {
      const { amount } = priceArr.find(({ type }) => type === 'TOTAL_PRICE') || {}
      return `${idx}.Total price for fare type ${name}: ${amount} for ${flightMetaInfo?.numOfTravellers || 1}`
    })
    return [
      `${
        price?.length ? `we have ${price.length} different pricing options. Pricing info are as follows:` : ''
      }`,
      ...priceDetails,
    ].join('\n')
  } else {
    const priceArr = []
    const { amount, taxesAndFees } = price || {}
    if (amount) {
      priceArr.push(`Total Price is ${amount}`)
    }
    if (taxesAndFees) {
      priceArr.push(`, taxes and fees is ${taxesAndFees}`)
    }

    priceArr.push(` for ${flightMetaInfo?.numOfTravellers || 1} travellers`)
    return priceArr.join('')
  }
}

const getFlightDetailsPageUrl = (flightMetaInfo, flightDetails, isExpressDeal = false) => {
  const { priceKey, itemKey } = flightDetails || {}
  if (window && flightMetaInfo?.loggingInfo?.slicesUrl && priceKey && itemKey) {
    const {
      numOfAdults,
      numOfChildren,
      numOfLapInfants,
      tripType,
      searchKey,
      cabinClass,
      slicesUrl,
      searchType,
    } = flightMetaInfo.loggingInfo

    if (!isExpressDeal) {
      return `${window.location.origin}/m/fly/search/${slicesUrl}/details/${priceKey}?&num-adults=${numOfAdults}&num-children=${numOfChildren}&num-infants=${numOfLapInfants}&itinerary-type-code=${tripType}&cabin-class=${cabinClass}&search-type=${searchType}&price-key=${priceKey}&item-key=${itemKey}&search-key=${searchKey}`
    } else {
      return `${window.location.origin}/m/fly/search/${slicesUrl}/deals?num-adults=${numOfAdults}&num-children=${numOfChildren}&num-infants=${numOfLapInfants}&itinerary-type-code=${tripType}&cabin-class=${cabinClass}&search-type=${searchType}&price-key=${priceKey}&item-key=${itemKey}&search-key=${searchKey}`
    }
  }

  return ''
}

export const getFlightsDetails = (details, pageSource, flightMetaInfo) => {
  const flightDetailsPageUrl = getFlightDetailsPageUrl(flightMetaInfo, details)
  const currency = details?.price?.[0]?.currencyCode || 'USD'
  const { price, slices, fareBrands, marketingAirlines, voidWindowInfo = {} } = details || {}
  if (pageSource === 'listings') {
    const flightDetails = [
      '### A FLIGHT DETAILS START - BELOW ###',
      getFlightInfo(slices, { marketingAirlines, pageSource }),
      `the currency is ${currency}`,
      getFlightPrice(fareBrands || price?.[0] || {}, flightMetaInfo),
    ]
    flightDetailsPageUrl && flightDetails.push(...[`the flight details page url is ${flightDetailsPageUrl}`])
    if (voidWindowInfo?.hoursLeft) {
      flightDetails.push(`free cancellation within next ${voidWindowInfo.hoursLeft} hours of booking`)
    }
    flightDetails.push('### A FLIGHT DETAILS END - ABOVE ###')
    return flightDetails.join(',\n')
  } else {
    const policy = details?.baggage?.disclaimer
    return `
    ${getFlightInfo(slices)},
    the currency is ${currency},
    ${getFlightPrice(price?.[0], flightMetaInfo)},
    policy is ${policy}
    `
  }
}

export const getFlightsDetailsForListing = (listings = [], flightMetaInfo) => {
  const flightListings = listings.reduce((accListings, currentListings) => {
    // Limit the number of flights we pass to the prompt to avoid prompt stuffing.
    if (accListings.length > 15) return accListings

    const { isExpressDeal, tripType, pricingInfo, slice } = currentListings || {}
    if (!isExpressDeal) {
      accListings.push(getFlightsDetails(currentListings, 'listings', flightMetaInfo))
    } else if (isExpressDeal) {
      const flightDetailsPageUrl = getFlightDetailsPageUrl(flightMetaInfo, currentListings, true)
      const expressDealDetails = `
        ### A FLIGHT DETAILS START - BELOW ###,
        This is an express deal,
        Trip type is a ${
          tripType === 'RT' ? 'round trip' : tripType === 'OW' ? 'one way' : 'multi destination'
        },
        Total Price is ${pricingInfo?.totalFare},
        ${getFlightExpressDealInfo(slice)},
        The flight details page url is ${flightDetailsPageUrl},
        ### A FLIGHT DETAILS END - ABOVE ###,
      `
      accListings.push(expressDealDetails)
    }
    return accListings
  }, [])
  const { travelDate, returnDate } = flightMetaInfo || {}
  const datesOfTravel = `The travel date is ${travelDate}.
  The return date is ${returnDate}.
  `
  return `${flightListings.join('.\n')}${datesOfTravel}`
}
